/**
 * RcmCompliance guardrail for autonomous Work Queue / Kai actions.
 * PRD-CS-002 Phase 1 — invoke before AI assignment and AI-driven completions.
 */

export class ComplianceGuardrailError extends Error {
  constructor(message, result = null) {
    super(message);
    this.name = 'ComplianceGuardrailError';
    this.result = result;
  }
}

function shouldUseShellApiProxy() {
  if (process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true') return false;
  if (typeof window === 'undefined' || !window.location) return false;
  const { hostname, port } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocal && (port === '3000' || port === '3001')) return false;
  if (isLocal && port !== '3160' && port !== '3161' && port !== '80' && port !== '443' && port !== '') {
    return false;
  }
  return true;
}

/** Shell actor label for guardrail audit (AgenticRCM :3160 vs AgenticRcmAgency :3161). */
export function getShellActor() {
  if (typeof window === 'undefined' || !window.location) return 'AgenticRCM';
  return window.location.port === '3161' ? 'AgenticRcmAgency' : 'AgenticRCM';
}

/** Resolve RcmCompliance API origin for guardrail calls from the shell. */
export function getComplianceApiBaseUrl() {
  const explicit = process.env.REACT_APP_RCM_COMPLIANCE_API_URL;
  if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
    return String(explicit).trim().replace(/\/$/, '');
  }

  if (shouldUseShellApiProxy()) {
    return '/rcm-api/compliance';
  }

  if (typeof window !== 'undefined' && window.location) {
    const { hostname, protocol, port } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocal && port === '3158') {
      return window.location.origin;
    }
    if (isLocal || process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true') {
      return `${protocol}//${hostname}:8158`;
    }
    return window.location.origin;
  }
  return 'http://localhost:8158';
}

export function extractDomainIdFromQueueItem(item) {
  if (!item || typeof item !== 'object') return null;
  const m = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
  if (m.domain_id != null && String(m.domain_id).length) return String(m.domain_id);
  for (const key of ['claim_id', 'charge_id', 'eligibility_request_id', 'request_id', 'eob_id', 'posting_id']) {
    if (m[key] != null && String(m[key]).length) return String(m[key]);
    if (item[key] != null && String(item[key]).length) return String(item[key]);
  }
  const id = String(item.id || '');
  const denialMatch = id.match(/^denial_mgmt-(.+)$/);
  if (denialMatch) return denialMatch[1];
  const remitMatch = id.match(/^remittance_exc-(.+)$/);
  if (remitMatch) return remitMatch[1];
  return null;
}

function readOrgId() {
  if (typeof window === 'undefined') return undefined;
  return (
    localStorage.getItem('organization_id') ||
    localStorage.getItem('selectedOrganizationId') ||
    localStorage.getItem('organizationId') ||
    undefined
  );
}

function readPracticeType() {
  if (typeof window === 'undefined') return undefined;
  return localStorage.getItem('practice_type') || localStorage.getItem('organization_practice_type') || undefined;
}

function readState() {
  if (typeof window === 'undefined') return undefined;
  return localStorage.getItem('practice_state') || localStorage.getItem('organization_state') || undefined;
}

/** Build guardrail request body from a queue row + action context. */
export function buildGuardrailPayloadFromQueueItem(item, action, extra = {}) {
  const meta = { ...(item?.metadata || {}), ...(item?.item_metadata || {}) };
  const domainId = extractDomainIdFromQueueItem(item) || item?.external_id || item?.id || 'unknown';
  const sourceService = item?.source_service || item?.external_service || item?.target_service || 'work_queue';

  let sourceEntityType = 'claim';
  if (meta.charge_id || sourceService === 'charge_capture') sourceEntityType = 'charge';
  if (meta.request_id || sourceService === 'prior_authorization') sourceEntityType = 'prior_auth';
  if (meta.eob_id || sourceService === 'remittance_advice') sourceEntityType = 'remittance';

  const claimPayload =
    meta.claim_payload ||
    meta.compliance_claim_payload || {
      procedure_codes: meta.procedure_codes || meta.cpt_codes,
      diagnosis_codes: meta.diagnosis_codes || meta.icd_codes,
      units: meta.units,
      days_since_service: meta.days_since_service,
      payer_name: meta.payer_name || item?.payer_name,
      pa_required: meta.pa_required,
      prior_auth_on_file: meta.prior_auth_on_file || meta.authorization_number,
      state: meta.state || readState(),
      practice_type: meta.practice_type || readPracticeType(),
    };

  return {
    actor: extra.actor || getShellActor(),
    action: action || 'queue_action',
    source_entity_type: sourceEntityType,
    source_entity_id: String(domainId),
    source_service: sourceService,
    practice_id: extra.practice_id || readOrgId() || 'default-practice',
    requested_by: extra.requested_by || 'agentic_rcm_work_queue',
    organization_id: extra.organization_id || readOrgId(),
    payer_id: meta.payer_id || item?.payer_id,
    payer_name: meta.payer_name || item?.payer_name,
    payer_class: meta.payer_class,
    specialty: meta.specialty,
    practice_type: meta.practice_type || readPracticeType(),
    state: meta.state || readState(),
    claim_payload: claimPayload,
  };
}

/** True when the action is an autonomous Kai side-effect that requires Type A guardrail. */
export function actionRequiresComplianceGuardrail({ action, assignment, updates, itemSnapshot }) {
  const assignType = assignment?.assigned_to_type;
  const isAiAssignment = assignType === 'ai';
  const isAiItem =
    itemSnapshot?.assigned_to_type === 'ai' ||
    itemSnapshot?.auto_assigned === true ||
    String(itemSnapshot?.assigned_to_name || '')
      .toLowerCase()
      .includes('kai');

  if (action === 'assign' && isAiAssignment) return true;
  if (action === 'bulk_assign' && isAiAssignment) return true;

  if (action === 'update' && isAiItem) {
    if (updates?.status === 'completed') return true;
    if (updates?.action_taken && String(updates.action_taken).trim()) return true;
  }
  return false;
}

/**
 * Call RcmCompliance POST /agents/guardrail-check.
 * Fail-open on network/HTTP errors unless REACT_APP_COMPLIANCE_GUARDRAIL_FAIL_CLOSED=true.
 */
export async function fetchComplianceGuardrailCheck(payload) {
  const failClosed = process.env.REACT_APP_COMPLIANCE_GUARDRAIL_FAIL_CLOSED === 'true';
  const token =
    (typeof window !== 'undefined' &&
      (localStorage.getItem('auth_token') || localStorage.getItem('token'))) ||
    '';

  const base = getComplianceApiBaseUrl().replace(/\/$/, '');
  const url = `${base}/api/v1/compliance/agents/guardrail-check`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (failClosed) {
        throw new ComplianceGuardrailError('Compliance guardrail unavailable', {
          allow_execution: false,
          disposition: 'BLOCK',
          fallback: true,
        });
      }
      return { allow_execution: true, disposition: 'ALLOW', fallback: true };
    }
    return await response.json();
  } catch (err) {
    if (err instanceof ComplianceGuardrailError) throw err;
    if (failClosed) {
      throw new ComplianceGuardrailError('Compliance guardrail check failed', {
        allow_execution: false,
        disposition: 'BLOCK',
        fallback: true,
        error: String(err?.message || err),
      });
    }
    return { allow_execution: true, disposition: 'ALLOW', fallback: true, error: String(err?.message || err) };
  }
}

/** Throws ComplianceGuardrailError when BLOCK; returns guardrail result otherwise. */
export async function enforceComplianceGuardrail(payload) {
  const result = await fetchComplianceGuardrailCheck(payload);
  if (result?.allow_execution === false || String(result?.disposition || '').toUpperCase() === 'BLOCK') {
    const findings = result?.check?.findings || [];
    const summary =
      findings[0]?.finding_text ||
      'Autonomous action blocked by RcmCompliance — route to human review.';
    throw new ComplianceGuardrailError(summary, result);
  }
  return result;
}
